"use client";

import { motion, useInView } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { useRef } from "react";
import { sharedSectionSubtitleClassName, sharedSectionTitleClassName } from "./section-header-styles";

import cheddarLogo from "../../app/images/cheddar.png";
import finvizLogo from "../../app/images/Adobe Express - file.png";
import optionStratLogo from "../../app/images/optionstart.png";
import seekingAlphaLogo from "../../app/images/seeking-alpha.png";
import tradingViewLogo from "../../app/images/tradingview-nct7p51k07roxa6picopf.webp";
import yahooFinanceLogo from "../../app/images/yahoo-finance-e577cb16.png";
import chatgptLogo from "../../app/images/ChatGPT-Logo.png";
import claudeLogo from "../../app/images/claude.png";

/* ── Minimalistic SVG logo components ── */

type Competitor = {
  name: string;
  tier: string;
  price: string;
  logo: StaticImageData;
  logoClassName?: string;
  stackedLogo?: StaticImageData;
  stackedLogoClassName?: string;
};

/* ── Data ── */

const COMPETITORS: Competitor[] = [
  {
    name: "TradingView",
    tier: "Premium",
    price: "$49.95",
    logo: tradingViewLogo,
    logoClassName: "opacity-90",
  },
  {
    name: "Cheddar Flow",
    tier: "Professional",
    price: "$99",
    logo: cheddarLogo,
    logoClassName: "opacity-90 scale-[1.35]",
  },
  {
    name: "OptionStrat",
    tier: "Live Flow",
    price: "$99.99",
    logo: optionStratLogo,
    logoClassName: "opacity-92",
  },
  {
    name: "Finviz",
    tier: "Elite",
    price: "$39.50",
    logo: finvizLogo,
    logoClassName: "opacity-90 scale-[1.28]",
  },
  {
    name: "Yahoo Finance",
    tier: "Plus",
    price: "$29.95",
    logo: yahooFinanceLogo,
    logoClassName: "opacity-92",
  },
  {
    name: "Seeking Alpha",
    tier: "Premium",
    price: "$19.99",
    logo: seekingAlphaLogo,
    logoClassName: "opacity-95 scale-[1.05]",
  },
  {
    name: "ChatGPT or Claude",
    tier: "Pro",
    price: "$20",
    logo: chatgptLogo,
    logoClassName: "opacity-90 scale-[1.8]",
    stackedLogo: claudeLogo,
    stackedLogoClassName: "opacity-90 scale-100",
  },
];

type CopyLine = { text: string; bold: string };
// Benefit bullets can carry a "tone" that colors the dot to match the product
// line: purple = Skills, emerald = Signals (and the generic value lines), icy
// sky = Alerts/Automations. The dot color itself signals which product the
// line is about, no separate tag needed.
type BenefitLine = CopyLine & { tone?: "skill" | "signal" | "alert" };

const PAIN_LINES: CopyLine[] = [
  { bold: "Six tabs open", text: " before your first decision of the day." },
  { bold: "2+ hours of prep", text: " for a single trade idea." },
  { bold: "Copy-pasting into ChatGPT", text: " for answers it can't verify." },
  { bold: "$300+/month", text: " in subscriptions before you place a single trade." },
  { bold: "5+ sources", text: " manually re-checked before every entry." },
  { bold: "No second pass.", text: " Your thesis goes unchallenged." },
  { bold: "Research across tools", text: " that never connect to each other." },
  { bold: "You're the synthesis.", text: " Every tool's output lives in your head." },
];

const BENEFIT_LINES: BenefitLine[] = [
  { bold: "3000+ data sources", text: ", one unified agent." },
  { bold: "Cross-synthesis", text: " finds confluences you'd miss." },
  { bold: "2 hours → 12 minutes.", text: " Cut research time by 10x." },
  { bold: "Frontier AI models", text: " doing the heavy lifting." },
  { bold: "Exact trade setups", text: " confirmed or denied with real data." },
  { bold: "Skills", text: ": pro-built research you reuse in one click.", tone: "skill" },
  {
    bold: "Signals",
    text: ": winning trades, full entry and exit.",
    tone: "signal",
  },
  {
    bold: "Alerts and dashboards",
    text: " watching the market for you 24/7.",
    tone: "alert",
  },
];

const ease = [0.23, 1, 0.32, 1] as const;

export function ResearchCostSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, margin: "-80px" });

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-black pt-[72px] pb-[69px] md:pt-[72px] md:pb-[69px]"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="mx-auto max-w-7xl px-6">
        {/* ── Title + Subtitle ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="text-left"
        >
          <h2 className={sharedSectionTitleClassName}>
            Your workflow is costing you{" "}
            <span className="text-red-500">money</span>.
          </h2>
          <p className={sharedSectionSubtitleClassName}>
            By the time you&apos;ve finished researching across 7 different
            tools, the Xynth trader got the alert and took your opportunity.
          </p>
        </motion.div>

        {/* ── Three-Panel Layout ── */}
        <div className="mt-12 overflow-hidden rounded-[28px] border border-white/[0.12] bg-black/40 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_28px_80px_rgba(0,0,0,0.5)]">
          <div className="grid grid-cols-1 md:grid-cols-3">

            {/* ─── Panel 1: Your Current Stack ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15, ease }}
              className="flex h-full flex-col bg-white/[0.01] p-6 md:border-r md:border-white/[0.06]"
            >
              <div className="flex h-11 items-center">
                <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/46">
                  Your current stack
                </h3>
              </div>

              <div className="mt-5 flex flex-1 flex-col justify-between gap-4">
                {COMPETITORS.map((c) => (
                  <div key={c.name} className="grid grid-cols-[2.25rem_minmax(0,1fr)] items-start gap-x-3">
                    {c.stackedLogo ? (
                      <div className="relative flex size-9 shrink-0 items-center justify-center">
                        <div className="absolute left-0 top-0 z-0 size-[26px] overflow-hidden rounded-md bg-white ring-1 ring-white/20">
                          <Image
                            src={c.stackedLogo}
                            alt="Claude logo"
                            fill
                            sizes="26px"
                            className={`object-contain p-[2px] ${c.stackedLogoClassName ?? ""}`}
                          />
                        </div>
                        <div className="absolute bottom-0 right-0 z-10 size-[26px] overflow-hidden rounded-md bg-white ring-1 ring-white/20">
                          <Image
                            src={c.logo}
                            alt="ChatGPT logo"
                            fill
                            sizes="26px"
                            className={`object-contain p-[2px] ${c.logoClassName ?? ""}`}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-white/20">
                        <Image
                          src={c.logo}
                          alt={`${c.name} logo`}
                          fill
                          sizes="36px"
                          className={`object-contain p-[5px] ${c.logoClassName ?? ""}`}
                        />
                      </div>
                    )}
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="block truncate text-[13px] font-medium leading-none tracking-[-0.01em] text-white/90">
                          {c.name}
                        </span>
                        <span className="mt-1 block text-[10px] leading-none text-white/40">
                          {c.tier}
                        </span>
                      </div>
                      <div className="w-[82px] shrink-0 text-right">
                        <span className="block text-[12.5px] font-medium leading-none text-white/46 line-through decoration-white/18 decoration-[1.2px]">
                          {c.price}
                          <span className="text-[10px] text-white/26">/mo</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex h-10 items-center border-t border-white/[0.05]">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[11px] font-medium text-white/28">
                    Monthly total
                  </span>
                  <span className="text-[16px] font-semibold tracking-[-0.02em] text-red-500">
                    $358<span className="text-[11px] text-red-500/60">+/mo</span>
                  </span>
                </div>
              </div>
            </motion.div>

            {/* ─── Panel 2: What It's Costing You ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3, ease }}
              className="flex h-full flex-col bg-white/[0.01] p-6 md:border-r md:border-white/[0.06]"
            >
              <div className="flex h-11 items-center">
                <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/46">
                  What it&apos;s costing you
                </h3>
              </div>

              <div className="mt-5 flex flex-1 flex-col">
                <div className="flex flex-1 flex-col justify-between">
                {PAIN_LINES.map((line) => (
                    <div key={line.bold} className="flex items-start gap-2.5">
                      <span className="mt-[3px] shrink-0 text-[12px] font-semibold leading-none text-red-400">
                        ✕
                      </span>
                      <p className="text-[13px] leading-snug tracking-[-0.01em] text-white/68">
                        <span className="font-semibold text-white/90">{line.bold}</span>{line.text}
                      </p>
                    </div>
                ))}
                </div>
              </div>

              <div className="mt-8 flex h-10 items-center border-t border-white/[0.05]">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[11px] font-medium text-white/28">
                    Time lost
                  </span>
                  <span className="text-[16px] font-semibold tracking-[-0.02em] text-red-500">
                    ~15hrs<span className="text-[11px] text-red-500/60">/week</span>
                  </span>

                </div>
              </div>
            </motion.div>

            {/* ─── Panel 3: Xynth ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.5, ease }}
              className="relative flex h-full flex-col bg-white/[0.03] p-6 shadow-[inset_0_0_80px_rgba(255,255,255,0.018),0_0_90px_rgba(255,255,255,0.022)]"
            >
              <div className="flex h-11 items-center justify-center">
                <Image
                  src="/logo.png"
                  alt="Xynth"
                  width={44}
                  height={44}
                  className="rounded-xl"
                />
              </div>

              <div className="mt-5 flex flex-1 flex-col">
                <div className="flex flex-1 flex-col justify-between">
                {BENEFIT_LINES.map((line) => (
                    <div key={line.bold} className="flex items-start gap-2.5">
                      {/* Same-size dot for every row so bullet text aligns
                          perfectly. The dot color maps to the product line:
                          purple = Skills, icy sky = Alerts/Automations, emerald
                          = Signals + the generic value lines. */}
                      <span
                        className={`mt-[7px] size-1.5 shrink-0 rounded-full ${
                          line.tone === "skill"
                            ? "bg-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.6)]"
                            : line.tone === "alert"
                              ? "bg-sky-300 shadow-[0_0_10px_rgba(186,230,253,0.55)]"
                              : "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.55)]"
                        }`}
                      />
                      <p className="text-[13px] leading-snug tracking-[-0.01em] text-white/92">
                        <span className="font-semibold">{line.bold}</span>{line.text}
                      </p>
                    </div>
                ))}
                </div>
              </div>

              <div className="mt-8">
                <Link href="/signup" className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-white px-4 text-[13px] font-medium text-black transition-colors duration-200 hover:bg-zinc-200">
                  Try at $0/month
                  <ChevronRight className="size-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
